import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import TrendIndicator from './TrendIndicator';

/**
 * SalesChart - Reusable pie chart for sales analytics
 * @param {Array} data - Array of items with sales data
 * @param {string} type - "best" or "slow" (for trend interpretation)
 * @param {string} metricUnit - Unit of measurement (e.g., 'items', 'lbs', 'jars')
 */
const SalesChart = ({ data, type = 'best', metricUnit = 'items' }) => {
    // Color palette for pie slices
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D', '#8DD1E1', '#D084D0'];

    // Format data for Recharts
    const chartData = data.map((item, index) => {
        // Keep actual value for display
        const actualValue = item.total_issued;
        // Use a minimum value for chart rendering (0.1% of max value, or 1 if all zeros)
        const maxValue = Math.max(...data.map(d => d.total_issued));
        const minRenderValue = maxValue > 0 ? maxValue * 0.001 : 1;
        const renderValue = actualValue > 0 ? actualValue : minRenderValue;

        // Abbreviate manufacturer name for labels (first 12 chars)
        const manufacturer = item.manufacturer || '';
        const shortManufacturer = manufacturer.length > 12 ? manufacturer.substring(0, 12) + '...' : manufacturer;

        return {
            name: item.description || item.sku || item.gtin,
            manufacturer: manufacturer,
            shortManufacturer: shortManufacturer,
            value: renderValue,  // Used for pie chart rendering
            actualValue: actualValue,  // Used for labels and tooltips
            trend: item.trend,
            trend_percentage: item.trend_percentage,
            fullItem: item,
        };
    });

    // Format number with thousand separators
    const formatNumber = (value) => {
        if (metricUnit === '$') {
            // Format as currency
            return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        if (metricUnit === 'lbs') {
            return value.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return Math.round(value).toLocaleString('en-US');
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const displayValue = formatNumber(data.actualValue);  // Use actualValue for display
            return (
                <div
                    style={{
                        background: '#ffffff',
                        padding: '12px',
                        border: '2px solid #333',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                        color: '#333',
                    }}
                >
                    <p style={{ margin: 0, fontWeight: 'bold', marginBottom: '6px', color: '#000', fontSize: '13px' }}>
                        {data.name}
                    </p>
                    {data.manufacturer && (
                        <p style={{ margin: 0, color: '#666', fontSize: '11px', marginBottom: '4px', fontStyle: 'italic' }}>
                            Manufacturer: {data.manufacturer}
                        </p>
                    )}
                    <p style={{ margin: 0, color: '#333', fontSize: '12px' }}>
                        {metricUnit === 'lbs' ? 'Weight' : metricUnit === '$' ? 'Profit' : 'Quantity'}: <strong style={{ color: '#000' }}>{metricUnit === '$' ? displayValue : `${displayValue} ${metricUnit}`}</strong>
                    </p>
                    <div style={{ marginTop: '6px' }}>
                        <TrendIndicator
                            trend={data.trend}
                            percentage={data.trend_percentage}
                            type={type}
                        />
                    </div>
                </div>
            );
        }
        return null;
    };

    // Custom label for pie slices - show absolute value with manufacturer
    const renderLabel = (entry) => {
        const value = formatNumber(entry.actualValue);
        const mfg = entry.shortManufacturer;
        // Show manufacturer on second line if available
        return mfg ? `${value}\n${mfg}` : value;
    };

    return (
        <ResponsiveContainer width="100%" height={300}>
            <PieChart>
                <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                >
                    {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    iconType="circle"
                    wrapperStyle={{ width: '45%' }}
                    formatter={(value, entry) => {
                        const data = entry.payload;
                        return (
                            <span style={{ fontSize: '12px', display: 'inline-block', maxWidth: '100%', wordWrap: 'break-word' }}>
                                {value.length > 80 ? `${value.substring(0, 80)}...` : value}
                            </span>
                        );
                    }}
                />
            </PieChart>
        </ResponsiveContainer>
    );
};

export default SalesChart;
